import React, { useState, useEffect } from 'react';
import { Contract, formatAsCurrency, getPolicyCoverEndpoint, InsuranceDetails, VehicleDetails } from '@cp-mx/common';
import { useAnalyticsActionTracker } from '../../../../../analytics';
import { AccordionItem, DefinitionListHorizontal, DefinitionListItem, Spinner } from '@cp-shared-19/frontend-ui';
import { useComponentTracking } from '../../../../../analytics/useComponentTracking';
import { useTranslation } from 'react-i18next';
import { Button, Notification, Modal, Tooltip } from '@vwfs-bronson/bronson-react';
import { CpDataApi } from '../../../../../cp-xhr';
import { ErrorNotification } from '../../../../notifications/error/ErrorNotification';
import { useInsuranceDetails, useInsuranceFooter } from '../useInsuranceDetails';
import moment from 'moment';

const ModalError: React.FC<{
    shown: boolean;
    onClose: () => void;
}> = ({ shown, onClose }) => {
    const { t } = useTranslation('insurance-details');
    return (
        <Modal
            className={'c-modal u-text-left'}
            style={{ height: '80%' }}
            shown={shown}
            testId={'error-modal'}
            onClose={() => onClose()}
        >
            <Notification
                className={'u-text-left'}
                testId={'error-notification'}
                status="error"
                title={t('error.noConnection.title')}
                visible={true}
                showCloseButton={false}
            >
                {t('error.noConnection.text')}
            </Notification>
        </Modal>
    );
};

const ModalIframe: React.FC<{
    shown: boolean;
    url: string;
    onClose: () => void;
}> = ({ shown, url, onClose }) => {
    const { t } = useTranslation('insurance-details');
    return (
        <Modal
            className={'c-modal  c-modal--large'}
            style={{ height: '80%' }}
            shown={shown}
            testId={'show-modal'}
            onClose={() => onClose()}
        >
            {!url ? (
                <>
                    <Spinner center />
                    {t('loadSpinnerText')}
                </>
            ) : (
                <div style={{ height: '500px' }}>
                    <iframe title="Policy" style={{ width: '100%', height: '100%' }} src={url}></iframe>
                </div>
            )}
        </Modal>
    );
};

export const InsuranceDetailsUi: React.FC<{ vehicleDetails?: VehicleDetails; title?: string; contract?: Contract }> = ({
    vehicleDetails,
    title,
    contract,
}) => {
    const { t } = useTranslation('insurance-details');
    const [width, setWidth] = useState(window.innerWidth);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
    const [shownErrorModal, setShownErrorModal] = useState(false);
    const [shownIframeModal, setShownIframeModal] = useState(false);
    const [url, setUrl] = useState('');
    const [isError, setIsError] = useState<boolean>(false);
    const { setInsuranceFooter } = useInsuranceFooter();
    const { onAction: onOpenAccordion } = useAnalyticsActionTracker('onOpenContractAccordion');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { onAction: onSuccess } = useAnalyticsActionTracker('onRequestCertificateSuccess');
    const { onAction: onError } = useAnalyticsActionTracker('onRequestCertificateSubmitFailed');
    const { onAction: onApiError } = useAnalyticsActionTracker('onRequestCertificateSubmitAPIError');
    const errorNotification = (
        <ErrorNotification
            testId={'no-connection-error'}
            headline={t('error.noConnection.title')}
            text={t('error.noConnection.text')}
        />
    );

    const {
        data: insuranceDetails,
        isLoading: isLoadingInsurance,
        loadingError: loadingErrorInsurance,
    } = useInsuranceDetails(contract?.contractNumber ? contract?.contractNumber : '', vehicleDetails?.vinCode);
    useComponentTracking(
        `contract-insurance-details-${contract?.contractLabel}`,
        !!(insuranceDetails && insuranceDetails.statusOfService !== 'Cancelada'),
        'contracts',
        'Accordion',
    );

    useEffect(() => {
        if (!isLoadingInsurance) {
            if (loadingErrorInsurance && loadingErrorInsurance.message.includes('404')) {
                setIsLoading(false);
            } else {
                if (insuranceDetails && insuranceDetails.statusOfService !== 'Cancelada') {
                    setInsuranceFooter(true);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                    setIsError(true);
                }
            }
        }
    }, [isLoadingInsurance, insuranceDetails, loadingErrorInsurance]);

    const handleResize = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
    }, []);

    const getFirstListItem = (insuranceDetails: InsuranceDetails): DefinitionListItem[] => {
        const items: DefinitionListItem[] = [];
        if (insuranceDetails.policyNumber) {
            items.push({
                label: t('policyNumber'),
                value: insuranceDetails.policyNumber,
                testId: 'policyNumber',
            });
        }
        if (insuranceDetails.insuranceCompany) {
            items.push({
                label: t('insuranceCompany'),
                value: insuranceDetails.insuranceCompany,
                testId: 'policyNumber',
                tooltip: t('insuranceCompanyTooltip'),
            });
        }
        if (insuranceDetails.contactTelephone) {
            items.push({
                label: t('contactTelephone'),
                value: insuranceDetails.contactTelephone,
                testId: 'contactTelephone',
                tooltip: t('contactTelephoneTooltip'),
            });
        }
        if (insuranceDetails.coverageType) {
            items.push({
                label: t('coverageType'),
                value: insuranceDetails.coverageType,
                testId: 'coverageType',
                tooltip: t('coverageTypeTooltip'),
            });
        }
        return items;
    };

    const getSecondListItem = (insuranceDetails: InsuranceDetails): DefinitionListItem[] => {
        const items: DefinitionListItem[] = [];
        if (insuranceDetails.statusOfService) {
            items.push({
                label: t('statusOfService'),
                value: insuranceDetails.statusOfService,
                testId: 'statusOfService',
            });
        }
        if (insuranceDetails.validityDate) {
            items.push({
                label: t('validityDate'),
                value: insuranceDetails.validityDate,
                testId: 'validityDate',
                tooltip: t('validityDateTooltip'),
            });
        }
        return items;
    };

    const getThirdListItem = (insuranceDetails: InsuranceDetails): DefinitionListItem[] => {
        const items: DefinitionListItem[] = [];
        if (insuranceDetails.typeOfPayment) {
            items.push({
                label: t('typeOfPayment'),
                value: insuranceDetails.typeOfPayment,
                testId: 'typeOfPayment',
                tooltip: t('typeOfPaymentTooltip'),
            });
        }
        if (insuranceDetails.typeOfPayment !== 'Financiado') {
            const contractDate = contract?.contractStartDate.split('-');
            const receiptCoverageDate = insuranceDetails.receipt?.coverageDate.split('-');
            if (receiptCoverageDate && contractDate) {
                const nextDueDate = moment(`${receiptCoverageDate[0]}-${contractDate[1]}-${contractDate[2]}`)
                    .format('DD/MMM/YYYY')
                    .replace('.', '');
                items.push({
                    label: t('nextDueDate'),
                    value: nextDueDate,
                });
                items.push({
                    label: t('amount'),
                    value: formatAsCurrency(insuranceDetails.receipt?.receiptAmount),
                    tooltip: t('amountToolTip'),
                });
            }
        }

        return items;
    };

    const getInsuranceCover = async (policyId: string, action: string): Promise<string> => {
        setIsLoadingButton(true);
        return await CpDataApi.get(getPolicyCoverEndpoint(policyId))
            .then(response => {
                setIsLoadingButton(false);
                setUrl('data:application/pdf;base64,' + response.data);
                if (action === 'download') {
                    onSuccess('Motor Car Insurance Policy - download');
                } else {
                    onSuccess('Motor Car Insurance Policy - display');
                }
                return 'data:application/pdf;base64,' + response.data;
            })
            .catch(error => {
                if (action === 'download') {
                    onApiError('Motor Car Insurance Policy - download', error);
                    onError('Motor Car Insurance Policy - download', error);
                } else {
                    onApiError('Motor Car Insurance Policy - display', error);
                    onError('Motor Car Insurance Policy - display', error);
                }
                setShownIframeModal(false);
                setIsLoadingButton(false);
                setShownErrorModal(true);
                return '';
            });
    };
    const startDownload = async (url: string, policyId: string) => {
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = `${policyId}.pdf`;
            link.click();
        }
    };

    const getInsuranceCoverDownload = async (policyId: string, action: string) => {
        setIsLoadingButton(true);
        const policy = insuranceDetails?.insuranceCompany === 'Chubb' ? policyId.replace(' ', '%20') : policyId;
        if (!url) {
            await getInsuranceCover(policy, action).then(response => {
                return startDownload(response, policy);
            });
        } else {
            if (!shownErrorModal) {
                onSuccess('Motor Car Insurance Policy - download');
                startDownload(url, policy);
            }
            setIsLoadingButton(false);
        }
    };

    const showInsuranceCover = async (policyId: string, action: string) => {
        setShownIframeModal(true);
        if (!url) {
            const policy = insuranceDetails?.insuranceCompany === 'Chubb' ? policyId.replace(' ', '%20') : policyId;
            await getInsuranceCover(policy, action);
        } else {
            onSuccess('Motor Car Insurance Policy - display');
        }
    };

    const displayLoadingSpinner = () => {
        const button = (
            <>
                <Button
                    className={width > 821 ? 'u-mu-p' : 'u-mh'}
                    full={!(width > 821)}
                    testId={'download-button'}
                    onClick={() => {
                        getInsuranceCoverDownload(insuranceDetails?.policyNumber || '', 'download');
                    }}
                >
                    {!isLoadingButton ? (
                        <>
                            {' '}
                            <Tooltip
                                alwaysVisible={false}
                                placement={'top'}
                                content={<>{t('downloadButtonTooltip')}</>}
                            >
                                <i className="c-icon  c-icon--[download]" role="img" aria-hidden="true" />
                            </Tooltip>
                        </>
                    ) : (
                        <Spinner small center />
                    )}
                </Button>
            </>
        );
        return button;
    };

    return (
        <>
            {isLoading && <Spinner center />}
            {isError && <AccordionItem title={title}>{errorNotification}</AccordionItem>}
            {insuranceDetails && insuranceDetails.statusOfService !== 'Cancelada' ? (
                <>
                    <ModalIframe shown={shownIframeModal} url={url} onClose={() => setShownIframeModal(false)} />
                    <ModalError shown={shownErrorModal} onClose={() => setShownErrorModal(false)} />
                    <div
                        onClick={() => {
                            if (!isOpen) {
                                onOpenAccordion('Motor Car Insurance Product');
                            }
                            setIsOpen(!isOpen);
                        }}
                        style={{ borderTop: '1px solid #4c5356' }}
                    >
                        <AccordionItem title={title}>
                            <DefinitionListHorizontal list={getFirstListItem(insuranceDetails)} />
                            <hr className="u-m-xsmall" />
                            <DefinitionListHorizontal list={getSecondListItem(insuranceDetails)} />
                            <hr className="u-m-xsmall" />
                            <DefinitionListHorizontal list={getThirdListItem(insuranceDetails)} />
                            {width > 821 ? (
                                <>
                                    <div className={'o-layout--right u-mt'}>
                                        <Button
                                            className={'u-mr'}
                                            secondary
                                            testId={'modal-button'}
                                            onClick={() => {
                                                showInsuranceCover(insuranceDetails?.policyNumber || '', 'visualize');
                                            }}
                                        >
                                            {t('visualizeButton')}
                                        </Button>
                                        {displayLoadingSpinner()}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Button
                                        full
                                        className={'u-m'}
                                        secondary
                                        testId={'modal-button'}
                                        onClick={() => {
                                            showInsuranceCover(insuranceDetails?.policyNumber || '', 'visualize');
                                        }}
                                    >
                                        {t('visualizeButton')}
                                    </Button>
                                    {displayLoadingSpinner()}
                                </>
                            )}
                        </AccordionItem>
                    </div>
                </>
            ) : (
                <></>
            )}
        </>
    );
};
